"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import DrawerSelectDate, { DrawerSelectDateRef } from "@/components/common/DrawerSelectDate"
import CalendarIcon from "@/components/icons/CalendarIcon"
import NightIcon from "@/components/icons/NightIcon"
import SearchIconV2 from "@/components/icons/SearchIconV2"
import UsersIcon from "@/components/icons/UsersIcon"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { hotelActions } from "@/lib/redux/features/hotel/reduce"

import { Button, Card, Col, Divider, Input, Row, theme } from "antd"
import dayjs from "dayjs"
import { FC, useEffect, useMemo } from "react"
import DrawerInputCustomerRoom, { DrawerInputCustomerRoomRef } from "../../FormSearch/mobile/DrawerInputCustomerRoom"
import DrawerSelectDestination, { DrawerSelectDestinationRef } from "../../FormSearch/mobile/DrawerSelectDestination"
import { getQueryStringSearchHotel } from "@/utils/hotel/getQueryStringSearchHotel"
import { useTERouter } from "@/hooks/app/useTERouter"
import { CardProps } from "antd/lib"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "next/navigation"
import {
  hasHotelSearchDatesInParams,
  isAffiliateHotelRenew,
} from "@/utils/hotel/hydrateHotelFormSearchFromApi"

export const searchHotelsDrawers: {
  selectDate: DrawerSelectDateRef | null,
  inputCustomersRooms: DrawerInputCustomerRoomRef | null,
  selectDestination: DrawerSelectDestinationRef | null
} = {
  selectDate: null,
  inputCustomersRooms: null,
  selectDestination: null,
}

type FormSearchMobileProps = CardProps & {
  onSearch?: () => void
}

const FormSearchMobile: FC<FormSearchMobileProps> = ({ onSearch, className, style, ...props }) => {
  const { getTranslatedText, getValidDayjs } = useTETranslation()
  const isDesktop = useIsDesktop()
  const token = theme.useToken().token
  const selectedDestination = useAppSelector(state => state.hotel.formSearch.location)
  const router = useTERouter()
  const dispatch = useAppDispatch()
  const [startDate, endDate] = useAppSelector(state => {
    return [state.hotel.formSearch.checkInDate, state.hotel.formSearch.checkOutDate]
  })
  const { t } = useTranslation()
  const urlSearchParams = useSearchParams()
  const skipAffiliateDateDefaults =
    isAffiliateHotelRenew(urlSearchParams) && !hasHotelSearchDatesInParams(urlSearchParams)

  const {
    adults,
    children,
    roomsNum
  } = useAppSelector(state => {
    return {
      adults: state.hotel.formSearch.adults,
      children: state.hotel.formSearch.children,
      roomsNum: state.hotel.formSearch.roomsNum,
    }
  })

  const [start, end] = useMemo(() => {
    return [getValidDayjs(startDate), getValidDayjs(endDate)]
  }, [startDate, endDate])

  useEffect(() => {
    // Desktop form mounts alongside this component; SelectCheckInOut owns date defaults there.
    if (isDesktop || startDate || skipAffiliateDateDefaults) return
    dispatch(hotelActions.selectDateCheckIn(dayjs().add(15, 'day').format('YYYY-MM-DD')))
    dispatch(hotelActions.selectDateCheckOut(dayjs().add(16, 'day').format('YYYY-MM-DD')))
  }, [isDesktop, startDate, skipAffiliateDateDefaults, dispatch])

  if (isDesktop) {
    return <></>
  }
  return (
    <Card
      {...props}
      className={className}
      size='small'
      style={{ zIndex: 1, boxShadow: token.boxShadow, ...style }}
    >
      <DrawerSelectDate
        value={[start, end]}
        range={true}
        minRangeLen={1}
        ref={(curr) => { searchHotelsDrawers.selectDate = curr; }}
        onChange={([from, to]) => {
          dispatch(hotelActions.selectDateCheckIn(from))
          if (to) {
            dispatch(hotelActions.selectDateCheckOut(to))
          }
        }}
      />
      <DrawerInputCustomerRoom ref={(curr) => { searchHotelsDrawers.inputCustomersRooms = curr; }} />
      <DrawerSelectDestination ref={(curr) => { searchHotelsDrawers.selectDestination = curr; }} />
      <Row>
        <Col span={24}>
          <Input
            onClick={() => searchHotelsDrawers.selectDestination?.open()}
            styles={{
              prefix: { padding: 0 },
              input: { fontSize: "1.8rem" },
            }}
            prefix={<SearchIconV2 style={{ fontSize: "1.8rem" }} />}
            style={{ height: "6.74rem", alignItems: "center", width: "100%" }}
            size='large'
            variant='borderless'
            value={getTranslatedText(selectedDestination?.nameLang?.vi, selectedDestination?.nameLang?.en)}
            placeholder={t("hotel.destination_search_placeholder")}
          />
        </Col>
      </Row>
      <Divider style={{ marginTop: 0, marginBottom: '1rem' }} />
      <div
        className="hotels__searching--date-field"
        role="button"
        tabIndex={0}
        onClick={() => searchHotelsDrawers.selectDate?.open()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            searchHotelsDrawers.selectDate?.open()
          }
        }}
      >
        <CalendarIcon className="hotels__searching--date-field-icon" />
        <Row wrap={false} className="hotels__searching--date-row" justify='space-between' align='middle' gutter={[12, 12]}>
          <Col flex={1} onClick={() => searchHotelsDrawers.selectDate?.open('from')}>
            <Row gutter={[5, 5]} wrap={false}>
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  {start.format('DD')}
                </Title>
              </Col>
              <Col>
                <Row>
                  <Text strong style={{ fontSize: '1.4rem' }}>
                    {start.format('MMMM')}
                  </Text>
                </Row>
                <Row>
                  <Text type='secondary' strong style={{ fontSize: '1.4rem' }}>
                    {start.format('dd')}
                  </Text>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row justify="center">
              <NightIcon style={{ color: token.colorPrimary, fontSize: '2rem' }} />
            </Row>
            <Row justify="center">
              <Text>{end.diff(start, 'd') || 1}</Text>
            </Row>
          </Col>
          <Col flex={1} onClick={() => searchHotelsDrawers.selectDate?.open('to')}>
            <Row gutter={[5, 5]} wrap={false} justify="end">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  {end.format('DD')}
                </Title>
              </Col>
              <Col>
                <Row>
                  <Text strong style={{ fontSize: '1.4rem' }}>
                    {end.format('MMMM')}
                  </Text>
                </Row>
                <Row>
                  <Text strong type='secondary' style={{ fontSize: '1.4rem' }}>
                    {end.format('dd')}
                  </Text>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <div
        className="hotels__searching--guest-field"
        role="button"
        tabIndex={0}
        onClick={() => searchHotelsDrawers.inputCustomersRooms?.open()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            searchHotelsDrawers.inputCustomersRooms?.open()
          }
        }}
      >
        <UsersIcon className="hotels__searching--guest-field-icon" />
        <Text>
          {roomsNum} {t("common.room")} • {adults} {t("common.adult")} • {children} {t("common.child")}
        </Text>
      </div>
      <Divider style={{ marginBlock: '1rem' }} />
      <Button
        size='large'
        type='primary'
        className="hotels__searching--search-btn"
        block
        onClick={() => {
          const searchString = getQueryStringSearchHotel()
          router.push(`/hotels/result?${searchString}`)
          onSearch && onSearch()
        }}
      >
        {t("common.search")}
      </Button>
    </Card>
  )
}

export default FormSearchMobile